import { createContext } from "react";
import { CARD_DECK_IDS, DEFAULT_CARD_DECK, type CardDeckId } from "@bing/shared";
import cardBack from "../assets/card-back.svg";

/**
 * Card DECKS ("designs") — how each deck draws a card FACE (the front, what a player reads in their
 * own hand). Every deck shares ONE back (`CARD_BACK`, the operator's Back B, 2026-10-20).
 *
 *  - `standard` («استاندارد») draws the bundled classic SVG faces (`@letele/playing-cards`, Adrian
 *    Kennard's English pattern — `card-faces.ts` loads that chunk).
 *  - the other three are VECTOR image decks under `src/assets/decks/<art>/<CardId>.svg`. Vite
 *    content-hashes every file into `/assets/` (served `immutable` — `infra/nginx.conf`), so a face
 *    is downloaded once and kept; `deck-assets.ts` fetches and decodes a whole deck in the background
 *    and `Card` draws it only once all 52 are in (until then, «استاندارد»).
 *
 * ⚠️ THE IDS ARE THE STORED ONES, THE ART BEHIND THEM IS NEW (2026-10-20). A player's pick lives in
 * localStorage (`bing:deck`) and in every private table's `settings.deck`, which the game-server
 * validates against `CARD_DECK_IDS` (`@bing/shared`). So the ids stay and the art and names change:
 * `modern` keeps its name «مدرن» over new art (Kennard's faces, big index — replaced the Kenney
 * deck) and `cartoon` is «پارسه» (the four-colour deck — replaced a deck with no licence; the
 * operator's names, 2026-10-20). Nobody's pick is lost.
 *
 * `<CardId>` is our own 2-char id (`"AS"`, `"TD"`, `"2C"`) — it IS the face filename.
 * Provenance and licences: `src/assets/decks/ATTRIBUTION.md`.
 */
export interface CardDeck {
  readonly id: CardDeckId;
  /** Persian display name (the picker + waiting-room chip). */
  readonly name: string;
  /** CardId → the face's hashed URL; `null` for the bundled classic SVG deck (`standard`). */
  readonly faces: Readonly<Record<string, string>> | null;
  /** The card-back image url — the same for every deck. */
  readonly back: string;
}

/** The one card back (Back B, vector, inlined into the bundle: a back is never fetched). */
export const CARD_BACK: string = cardBack;

type Glob = Record<string, string>;
/** `../assets/decks/royal/KH.svg` → `KH`. */
function byCardId(files: Glob): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [file, url] of Object.entries(files)) {
    const id = /([^/]+)\.svg$/.exec(file)?.[1];
    if (id) out[id] = url;
  }
  return out;
}

// `import.meta.glob` takes literals only — one call per deck. `?url` + `eager`: the URL STRINGS are
// in the bundle (52 short paths a deck), the files are separate hashed assets fetched on demand
// (`vite.config.ts` keeps them from ever being inlined into a chunk).
const LEGIBLE = byCardId(
  import.meta.glob<string>("../assets/decks/legible/*.svg", {
    eager: true,
    query: "?url",
    import: "default",
  }),
);
const FOUR_COLOUR = byCardId(
  import.meta.glob<string>("../assets/decks/four-colour/*.svg", {
    eager: true,
    query: "?url",
    import: "default",
  }),
);
const ROYAL = byCardId(
  import.meta.glob<string>("../assets/decks/royal/*.svg", {
    eager: true,
    query: "?url",
    import: "default",
  }),
);

export const CARD_DECKS: Record<CardDeckId, CardDeck> = {
  standard: { id: "standard", name: "استاندارد", faces: null, back: CARD_BACK },
  modern: { id: "modern", name: "مدرن", faces: LEGIBLE, back: CARD_BACK },
  cartoon: { id: "cartoon", name: "پارسه", faces: FOUR_COLOUR, back: CARD_BACK },
  royal: { id: "royal", name: "سلطنتی", faces: ROYAL, back: CARD_BACK },
};

/** The decks in display order (standard first) — the picker iterates this. */
export const CARD_DECK_LIST: readonly CardDeck[] = CARD_DECK_IDS.map((id) => CARD_DECKS[id]);

/**
 * The FACE image url for a card in an IMAGE deck, or `null` for the classic SVG deck (whose face is
 * a React component, not an image). This is the file's own URL; what `Card` draws is the URL the
 * loader actually fetched (`loadedFaceSrc` in `deck-assets.ts`), which may carry a retry query.
 */
export function deckFaceSrc(deck: CardDeck, cardId: string): string | null {
  return deck.faces?.[cardId] ?? null;
}

/** The back image url for a deck id — one back for every deck. */
export function deckBack(_id?: CardDeckId | null): string {
  return CARD_BACK;
}

/**
 * The deck a table's cards render with. Provided by the online shell (from the
 * table's `settings.deck`) and by `App` for solo games (from the per-user offline
 * pref); the shared `Card` reads it via context so no table has to thread `deck`
 * through every card. Defaults to the standard deck when no provider is present.
 */
export const CardDeckContext = createContext<CardDeckId>(DEFAULT_CARD_DECK);
